import { useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldAlert, Loader2, AlertCircle, RefreshCw, X } from 'lucide-react';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { useStorageGroups, useStorageUpload } from '../hooks';
import type { UploadProgress } from '../hooks';
import { GroupSection } from '../components';
import type { StorageArcheConfig } from '../types';
import { createStorageQueryKeys } from '../types';

interface StorageBrowsePageProps {
  config: StorageArcheConfig;
}

export function StorageBrowsePage({ config }: StorageBrowsePageProps) {
  const hasRole = useArcheAdmin(config.domainAdminRole);

  const keys = useMemo(
    () => createStorageQueryKeys(config.queryKeyNamespace),
    [config.queryKeyNamespace],
  );

  const { data, isLoading, error } = useStorageGroups(config.api, keys.groups());
  const queryClient = useQueryClient();
  const { uploadFiles, retryFailedFiles, clearFailedFiles, failedFiles } = useStorageUpload(
    config.api,
  );

  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [pendingGroups, setPendingGroups] = useState<string[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [...keys.all] });
  }, [queryClient, keys.all]);

  const handleDelete = useCallback(
    async (key: string) => {
      if (!window.confirm(`Delete ${key}?`)) return;
      setActionError(null);
      try {
        await config.api.deleteFile(key);
        // Invalidate the specific prefix that contained this file
        const filePrefix = key.substring(0, key.lastIndexOf('/') + 1);
        queryClient.invalidateQueries({ queryKey: [...keys.files(filePrefix)] });
        // Also invalidate parent group listing (folder counts may change)
        const parentPrefix = filePrefix.replace(/[^/]+\/$/, '');
        if (parentPrefix !== filePrefix) {
          queryClient.invalidateQueries({ queryKey: [...keys.files(parentPrefix)] });
        }
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to delete file');
      }
    },
    [queryClient, keys, config.api],
  );

  const handleDeleteGroup = useCallback(
    async (prefix: string) => {
      const groupName = prefix.replace(/\/$/, '');
      const isSubGroup = groupName.includes('/');
      const label = isSubGroup ? 'sub-group' : 'group';
      if (
        !window.confirm(
          `Delete ${label} "${groupName}" and all files in it? This cannot be undone.`,
        )
      )
        return;
      setActionError(null);
      try {
        const result = await config.api.deleteGroup(prefix);
        invalidateAll();
        if (!result.success && result.failedKeys && result.failedKeys.length > 0) {
          const names = result.failedKeys.map((key) => key.split('/').pop() ?? key);
          setActionError(
            `Deleted ${result.deletedCount} file(s), but ${result.failedCount} failed: ${names.join(', ')}`,
          );
        }
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to delete group');
      }
    },
    [config.api, invalidateAll],
  );

  const handleCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name || !/^[a-zA-Z0-9_-]+$/.test(name)) return;

    // Create a .keep placeholder so the group persists in S3
    try {
      const { uploadUrl } = await config.api.getUploadUrl(
        `${name}/.keep`,
        'application/octet-stream',
        1,
      );
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: new Uint8Array(1),
      });
      invalidateAll();
    } catch {
      // Fall back to showing the group locally until a file is uploaded
      setPendingGroups((prev) => [...prev, name]);
    }

    setNewGroupName('');
    setShowNewGroup(false);
  }, [newGroupName, invalidateAll, config.api]);

  const handleUploadToGroup = useCallback(
    async (groupPrefix: string, files: FileList | null) => {
      if (!files || files.length === 0) return;
      setActionError(null);
      clearFailedFiles();

      const fileEntries = Array.from(files).map((file) => ({
        file,
        key: `${groupPrefix}${file.name}`,
      }));

      await uploadFiles(fileEntries, setUploadProgress);
      setUploadProgress(null);

      // Remove from pending if it was a pending group
      const groupName = groupPrefix.replace(/\/$/, '');
      setPendingGroups((prev) => prev.filter((g) => g !== groupName));

      // Invalidate once at the end, not per-file
      invalidateAll();
    },
    [uploadFiles, clearFailedFiles, invalidateAll],
  );

  const handleRetry = useCallback(async () => {
    setActionError(null);
    await retryFailedFiles(setUploadProgress);
    setUploadProgress(null);
    invalidateAll();
  }, [retryFailedFiles, invalidateAll]);

  if (!hasRole) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <ShieldAlert className="h-12 w-12 mb-3" />
        <p className="text-lg font-medium">Access required</p>
        <p className="text-sm">{config.accessDeniedMessage}</p>
      </div>
    );
  }

  // Merge server groups with locally-pending groups (not yet in S3)
  const serverGroupNames = new Set(data?.groups.map((g) => g.name) ?? []);
  const visiblePendingGroups = pendingGroups.filter((g) => !serverGroupNames.has(g));

  return (
    <div className="flex flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{config.title}</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowNewGroup(!showNewGroup)}>
              <Plus className="h-4 w-4 mr-1" />
              New Group
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showNewGroup && (
            <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/20">
              <Input
                placeholder="Group name (e.g. dfe-items)"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                className="max-w-xs"
              />
              <Button
                size="sm"
                disabled={!newGroupName.trim() || !/^[a-zA-Z0-9_-]+$/.test(newGroupName.trim())}
                onClick={handleCreateGroup}
              >
                Create
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowNewGroup(false)}>
                Cancel
              </Button>
            </div>
          )}

          {uploadProgress && uploadProgress.inProgress && (
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/20">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">
                Uploading {uploadProgress.completed} / {uploadProgress.total}
                {uploadProgress.failed > 0 && (
                  <span className="text-destructive ml-1">({uploadProgress.failed} failed)</span>
                )}
              </span>
            </div>
          )}

          {failedFiles.length > 0 && !uploadProgress?.inProgress && (
            <div className="flex flex-col gap-2 p-3 border border-destructive/30 rounded-lg bg-destructive/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium">{failedFiles.length} file(s) failed to upload</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="text-xs" onClick={handleRetry}>
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    aria-label="Dismiss failed uploads"
                    onClick={clearFailedFiles}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {failedFiles.slice(0, 10).map((f, i) => {
                  const name = f.key.split('/').pop() ?? f.key;
                  return (
                    <p key={i}>
                      {name}
                      {f.reason && <span className="text-destructive/70 ml-1">— {f.reason}</span>}
                    </p>
                  );
                })}
                {failedFiles.length > 10 && <p>and {failedFiles.length - 10} more</p>}
              </div>
            </div>
          )}

          {actionError && <p className="text-destructive text-sm">{actionError}</p>}

          {isLoading && <p className="text-muted-foreground">Loading...</p>}
          {error && (
            <p className="text-destructive">
              {error instanceof Error ? error.message : 'Failed to load files'}
            </p>
          )}

          {visiblePendingGroups.map((name) => (
            <GroupWithUpload
              key={name}
              name={name}
              prefix={`${name}/`}
              cdnBaseUrl={data?.cdnBaseUrl ?? undefined}
              onDelete={handleDelete}
              onDeleteGroup={handleDeleteGroup}
              onUpload={handleUploadToGroup}
              api={config.api}
              queryKeyAll={keys.all}
              queryKeyFiles={keys.files}
              acceptedFileTypes={config.acceptedFileTypes}
              uploading={uploadProgress?.inProgress ?? false}
            />
          ))}

          {data?.groups.map((group) => (
            <GroupWithUpload
              key={group.prefix}
              name={group.name}
              prefix={group.prefix}
              cdnBaseUrl={data.cdnBaseUrl ?? undefined}
              onDelete={handleDelete}
              onDeleteGroup={handleDeleteGroup}
              onUpload={handleUploadToGroup}
              api={config.api}
              queryKeyAll={keys.all}
              queryKeyFiles={keys.files}
              acceptedFileTypes={config.acceptedFileTypes}
              uploading={uploadProgress?.inProgress ?? false}
            />
          ))}

          {!isLoading && data?.groups.length === 0 && visiblePendingGroups.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              No groups yet. Click "New Group" to create one.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GroupWithUpload({
  name,
  prefix,
  cdnBaseUrl,
  onDelete,
  onDeleteGroup,
  onUpload,
  api,
  queryKeyAll,
  queryKeyFiles,
  acceptedFileTypes,
  uploading,
}: {
  name: string;
  prefix: string;
  cdnBaseUrl?: string;
  onDelete: (key: string) => void;
  onDeleteGroup: (prefix: string) => void;
  onUpload: (prefix: string, files: FileList | null) => Promise<void>;
  api: StorageBrowsePageProps['config']['api'];
  queryKeyAll: readonly string[];
  queryKeyFiles: (prefix: string) => readonly string[];
  acceptedFileTypes?: string;
  uploading: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-1">
      <GroupSection
        name={name}
        prefix={prefix}
        cdnBaseUrl={cdnBaseUrl}
        onDelete={onDelete}
        onDeleteGroup={onDeleteGroup}
        onUpload={onUpload}
        api={api}
        queryKeyAll={queryKeyAll}
        queryKeyFiles={queryKeyFiles}
        acceptedFileTypes={acceptedFileTypes}
        disabled={uploading}
      />
      <div className="flex justify-end px-1">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFileTypes}
          className="hidden"
          onChange={(e) => {
            onUpload(prefix, e.target.files);
            if (e.target) e.target.value = '';
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          className="text-xs"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <Plus className="h-3 w-3 mr-1" />
          Upload to {name}
        </Button>
      </div>
    </div>
  );
}
